/**
 * @file Les 3 gros boutons Power (rouge) / Speed (jaune) / Money (vert) avec niveau, multiplicateur, cout ; achat maintenu accelere.
 */
import { UPGRADE_IDS } from '@shared/enums';
import type { UpgradeId } from '@shared/enums';
import { formatBig, formatMult } from '@shared/format';
import { Component } from '../core/Component';
import { h, setText, toggleClass } from '../core/dom';
import { send } from '../core/feedback';
import { attachHoldRepeat } from '../core/holdRepeat';
import { currencyIcon } from '../core/icons';
import { uiIcon } from '../core/uiIcons';
import type { UiIconId } from '../core/uiIcons';
import type { UiContext } from '../UiContext';

/** Au bout de ce temps d'appui continu, chaque repetition achete par paquet de 10. */
const HOLD_BULK_MS = 1500;
const BULK_COUNT = 10;
const NOW = (): number => performance.now();
const ICONS: Record<UpgradeId, UiIconId> = { power: 'power', speed: 'speed', money: 'money' };

interface CardView {
  root: HTMLElement;
  level: HTMLElement;
  effect: HTMLElement;
  cost: HTMLElement;
}

export class UpgradePanel extends Component {
  private readonly cards = new Map<UpgradeId, CardView>();

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'upgrades');
  }

  protected build(): void {
    const { i18n, config } = this.ctx;
    for (const id of UPGRADE_IDS) {
      const level = h('span', { class: 'up-level' });
      const effect = h('div', { class: 'up-effect' });
      const cost = h('b', { class: 'up-cost-value' });
      const main = h(
        'div',
        { class: 'up-main', role: 'button', tabindex: 0, 'aria-label': i18n.t(`upgrade.${id}`) },
        h('div', { class: 'up-head' }, h('span', { class: 'up-icon' }, uiIcon(ICONS[id])), level),
        effect,
        h('div', { class: 'up-cost' }, currencyIcon('money'), cost),
      );
      const max = h('button', { class: 'up-max', type: 'button', 'data-ui-block': true, 'data-fast': true, onclick: () => this.buy(id, 'max') }, i18n.t('upgrade.buyMax'));
      const name = h('span', { class: 'up-name outlined-text' }, i18n.t(`upgrade.${id}`));
      const root = h('div', { class: `upgrade up-${id}`, 'data-ui-block': true, 'data-tutorial': `upgrade-${id}` }, name, main, max);
      this.cards.set(id, { root, level, effect, cost });
      this.el.append(root);

      // L'ecouteur "debut d'appui" doit etre pose AVANT attachHoldRepeat pour armer le chrono du paquet x10.
      let holdStart = 0;
      this.on(main, 'pointerdown', () => {
        holdStart = NOW();
      });
      const stop = (): void => {
        holdStart = 0;
      };
      this.on(main, 'pointerup', stop);
      this.on(main, 'pointerleave', stop);
      this.on(main, 'pointercancel', stop);
      this.addDisposer(
        attachHoldRepeat(main, {
          startMs: config.balance.upgrades.holdRepeatMs.start,
          minMs: config.balance.upgrades.holdRepeatMs.min,
          accel: config.balance.upgrades.holdRepeatMs.accel,
          onFire: () => {
            const first = holdStart > 0 && NOW() - holdStart < 40;
            const bulk = holdStart > 0 && NOW() - holdStart >= HOLD_BULK_MS;
            const count = bulk ? Math.max(1, Math.min(BULK_COUNT, this.ctx.sim.query.upgradeCard(id).maxAffordable)) : 1;
            return this.buy(id, count, !first);
          },
        }),
      );
      // Clavier (accessibilite) : Entree / Espace = un achat.
      this.on(main, 'keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.buy(id, 1);
        }
      });
    }
    // Retour d'achat : « pop » du multiplicateur + halo (Achat max : plus marque + « +N niv. »). Les achats AUTO restent discrets.
    this.listen('UpgradeBought', (e) => {
      if (e.auto) return;
      const view = this.cards.get(e.id);
      if (view) this.pop(view.root, e.count > 1 ? 'bought-max' : 'bought', e.count);
    });
    this.update(0);
  }

  /** Envoie l'achat ; un refus (pas assez d'argent) fait clignoter le cout en rouge + petite vibration (sauf repetition du maintien). */
  private buy(id: UpgradeId, count: number | 'max', repeat = false): boolean {
    const ok = send(this.ctx, { type: 'upgrade.buy', id, count }).ok;
    if (!ok && !repeat) {
      const view = this.cards.get(id);
      if (view) this.pop(view.root, 'denied', 0);
      this.ctx.haptic('tick');
    }
    return ok;
  }

  private pop(root: HTMLElement, cls: 'bought' | 'bought-max' | 'denied', count: number): void {
    root.classList.remove('bought', 'bought-max', 'denied');
    void root.offsetWidth; // relance l'animation CSS
    root.classList.add(cls);
    if (cls === 'bought-max') {
      const gain = h('span', { class: 'up-gain outlined-text' }, this.ctx.i18n.t('upgrade.gainLevels', { n: count }));
      gain.addEventListener('animationend', () => gain.remove(), { once: true });
      window.setTimeout(() => gain.remove(), 1200);
      root.append(gain);
    }
  }

  override update(_dt: number): void {
    const { i18n } = this.ctx;
    for (const card of this.ctx.sim.query.upgradeCards()) {
      const view = this.cards.get(card.id);
      if (!view) continue;
      setText(view.level, i18n.t('common.level', { level: card.level }));
      setText(view.effect, formatMult(card.multiplier));
      view.effect.title = card.maxed ? '' : `${formatMult(card.multiplier)} ➜ ${formatMult(card.nextMultiplier)}`;
      setText(view.cost, card.maxed ? i18n.t('common.max') : formatBig(card.cost));
      toggleClass(view.root, 'disabled', !card.affordable && !card.maxed);
      toggleClass(view.root, 'maxed', card.maxed);
    }
  }
}
